import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "./Navigation";
import { getTokenInternal } from "./useToken";
import LoginRequired from "./LoginRequired.js";
import { Info } from "lucide-react";

const TRUST_EXPLANATION =
  "Lets this friend mark items as found on your behalf while viewing your tracker. You still approve every claim before it counts.";

const FriendSearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [allFriends, setAllFriends] = useState([]);
  const [friendRequestsSent, setFriendRequestsSent] = useState(new Set());
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [whoTrustsMeIds, setWhoTrustsMeIds] = useState([]);
  const [grantedByMeIds, setGrantedByMeIds] = useState([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [confirmBlockId, setConfirmBlockId] = useState(null);
  const [trustInfoOpenId, setTrustInfoOpenId] = useState(null);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const resolveFriendAccountId = (friend) =>
    friend.user_id !== currentUserId ? friend.user_id : friend.friend_id;

  useEffect(() => {
    if (trustInfoOpenId === null) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(".trust-info-trigger")) {
        setTrustInfoOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [trustInfoOpenId]);

  useEffect(() => {
    getTokenInternal().then(t => {
      setIsLoggedIn(!!t);
      setTokenChecked(true);
    });
  }, []);

  useEffect(() => {
    const fetchTrustStatus = async () => {
      try {
        const token = await getTokenInternal();
        if (!token) return;
        setCurrentUserId(token.account.id);
        const [whoTrustsMeRes, grantedByMeRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_HOST}/api/trusted-claimers/who-trusts-me`, {
            headers: { Authorization: `Bearer ${token.access_token}` },
          }),
          fetch(`${process.env.REACT_APP_API_HOST}/api/trusted-claimers/granted-by-me`, {
            headers: { Authorization: `Bearer ${token.access_token}` },
          }),
        ]);
        if (whoTrustsMeRes.ok) {
          const data = await whoTrustsMeRes.json();
          setWhoTrustsMeIds(data.map((account) => account.account_id));
        }
        if (grantedByMeRes.ok) {
          const data = await grantedByMeRes.json();
          setGrantedByMeIds(data.map((account) => account.account_id));
        }
      } catch (error) {
        console.error("Error fetching trust status:", error.message);
      }
    };
    fetchTrustStatus();
  }, []);

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const token = await getTokenInternal();

        if (token) {
          const url = `${process.env.REACT_APP_API_HOST}/api/friendsList`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setAllFriends(data);
          }
        }
      } catch (error) {
        console.error("Error fetching pending friend requests:", error);
      }
    };

    // Fetch pending friend requests when the component mounts and whenever the user changes
    fetchFriends();
  }, []);




  useEffect(() => {
    const getBlockedUsers = async () => {
      try {
        const token = await getTokenInternal();

        if (token) {
          const url = `${process.env.REACT_APP_API_HOST}/api/blocked`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setBlockedUsers(data);
          }
        }
      } catch (error) {
        console.error("Error fetching blocked users:", error);
      }
    };

    // Fetch blocked users when the component mounts and whenever the user changes
    getBlockedUsers();
  }, []);




  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        const token = await getTokenInternal();
        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/api/accounts?query=${searchTerm}`,
          { headers: { Authorization: `Bearer ${token?.access_token}` } }
        );
        if (!response.ok) return;
        const data = await response.json();
        setSearchResults(data);
      } catch (error) {
        console.error("Error fetching search results:", error);
      }
    };

    if (searchTerm.trim() !== "") {
      fetchSearchResults();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);



  useEffect(() => {
    const fetchPendingRequests = async () => {
      try {
        const token = await getTokenInternal();

        if (token) {
          const url = `${process.env.REACT_APP_API_HOST}/api/pending-with-usernames`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setCurrentUser(token.account.username);
            setPendingRequests(data);
          }
        }
      } catch (error) {
        console.error("Error fetching pending friend requests:", error);
      }
    };

    // Fetch pending friend requests when the component mounts and whenever the user changes
    fetchPendingRequests();
  }, []);

  useEffect(() => {
    const fetchSentRequests = async () => {
      try {
        const token = await getTokenInternal();

        if (token) {
          const url = `${process.env.REACT_APP_API_HOST}/api/pending-sent-with-usernames`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setSentRequests(data);
          }
        }
      } catch (error) {
        console.error("Error fetching sent friend requests:", error);
      }
    };

    fetchSentRequests();
  }, []);

  const handleCancelSentRequest = async (request) => {
    try {
      const token = await getTokenInternal();
      const url = `${process.env.REACT_APP_API_HOST}/api/friends/cancel/${request.friend_id}`;
      await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
      });
      setSentRequests((prevRequests) =>
        prevRequests.filter((r) => r.id !== request.id)
      );
    } catch (error) {
      console.error("Error canceling sent friend request:", error);
    }
  };



  const handleFriendSelect = async (friend) => {
    setSelectedFriend(friend); // still update state for UI
    await handleFriendRequest(friend); // pass friend directly
  };

  const handleFriendRequest = async (friend) => {
    try {
      const token = await getTokenInternal();

      if (token && friend) {
        const url = `${process.env.REACT_APP_API_HOST}/api/friends/${friend.id}`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            friend_id: friend.id,
          }),
        });

        if (response.ok) {
          setFriendRequestsSent((prev) => new Set(prev).add(friend.id));

          // Refresh both lists: this covers the normal "new pending
          // request" case AND the case where the other person had
          // already requested us, which the backend auto-accepts
          // instead of creating a duplicate.
          const [sentRes, friendsRes] = await Promise.all([
            fetch(`${process.env.REACT_APP_API_HOST}/api/pending-sent-with-usernames`, {
              headers: { Authorization: `Bearer ${token.access_token}` },
            }),
            fetch(`${process.env.REACT_APP_API_HOST}/api/friendsList`, {
              headers: { Authorization: `Bearer ${token.access_token}` },
            }),
          ]);
          if (sentRes.ok) setSentRequests(await sentRes.json());
          if (friendsRes.ok) setAllFriends(await friendsRes.json());
        } else {
          console.error("Error sending friend request:", response.statusText);
        }
      }
    } catch (error) {
      console.error("Error sending friend request:", error);
    }
  };




const handleSearchedBlockUser = async (friend) => {
  try {
    const token = await getTokenInternal();


    // Block the user
    const blockUrl = `${process.env.REACT_APP_API_HOST}/api/block/${friend.id}`;
    await fetch(blockUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
    });

  } catch (error) {
    console.error("Error declining friend request:", error);
  }
};






const handleBlockUser = async (friend) => {
  try {
    const token = await getTokenInternal();

    // Decline friend request
    const declineUrl = `${process.env.REACT_APP_API_HOST}/api/friends/decline/${friend.user_id}`;
    await fetch(declineUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
    });
    setPendingRequests((prevRequests) =>
      prevRequests.filter((request) => request.id !== friend.id)
    );

    // Block the user
    const blockUrl = `${process.env.REACT_APP_API_HOST}/api/block/${friend.user_id}`;
    await fetch(blockUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
    });

    // Update the UI
    setPendingRequests((prevRequests) =>
      prevRequests.filter((request) => request.id !== friend.id)
    );

    // Fetch blocked user's information
    const blockedUserUrl = `${process.env.REACT_APP_API_HOST}/api/accounts/${friend.user_id}`;
    const blockedUserResponse = await fetch(blockedUserUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
    });

    if (blockedUserResponse.ok) {
      const blockedUserData = await blockedUserResponse.json();

      // Add the blocked user to the state
      setBlockedUsers((prevBlockedUsers) => [
        ...prevBlockedUsers,
        blockedUserData,
      ]);
    }
  } catch (error) {
    console.error("Error declining friend request:", error);
  }
};




  const handleAcceptFriendRequest = async (friend) => {
    try {
      const token = await getTokenInternal();

      if (token && friend) {
        const url = `${process.env.REACT_APP_API_HOST}/api/friends/accept/${friend.user_id}`;
        await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });

        // Update the UI
        setPendingRequests((prevRequests) =>
          prevRequests.filter((request) => request.id !== friend.id)
        );

        // Fetch the updated friend list after accepting the request
        const updatedFriendsResponse = await fetch(
          `${process.env.REACT_APP_API_HOST}/api/friendsList`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (updatedFriendsResponse.ok) {
          const updatedFriendsData = await updatedFriendsResponse.json();
          setAllFriends(updatedFriendsData);
        }
      }
    } catch (error) {
      console.error("Error accepting friend request:", error);
    }
  };





  const handleDeclineFriendRequest = async (friend) => {
    try {
      const token = await getTokenInternal();

      const url = `${process.env.REACT_APP_API_HOST}/api/friends/decline/${friend.user_id}`;
      await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
      });
      setPendingRequests((prevRequests) =>
        prevRequests.filter((request) => request.id !== friend.id)
      );
    } catch (error) {
      console.error("Error declining friend request:", error);
    }
  };

  const handleRemoveFriendRequest = async (friend) => {
    try {
      const token = await getTokenInternal();
      if (token && friend) {
        const url = `${process.env.REACT_APP_API_HOST}/api/friends/${friend.user_id}`;
        await fetch(url, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });
        const urls = `${process.env.REACT_APP_API_HOST}/api/friends/${friend.friend_id}`;
        await fetch(urls, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });

        // Update the UI
        setAllFriends((prevFriends) =>
          prevFriends.filter((friendItem) => friendItem.id !== friend.id)
        );
      }
    } catch (error) {
      console.error("Error removing friend:", error);
    }
  };

  const handleBlockFriend = async (friend) => {
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const friendAccountId = resolveFriendAccountId(friend);
      await Promise.all([
        fetch(`${process.env.REACT_APP_API_HOST}/api/friends/${friend.user_id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }),
        fetch(`${process.env.REACT_APP_API_HOST}/api/friends/${friend.friend_id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }),
        fetch(`${process.env.REACT_APP_API_HOST}/api/block/${friendAccountId}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }),
      ]);
      setAllFriends((prev) => prev.filter((f) => f.id !== friend.id));
      setConfirmBlockId(null);
      const blockedRes = await fetch(`${process.env.REACT_APP_API_HOST}/api/blocked`, {
        headers: { Authorization: `Bearer ${token.access_token}` },
      });
      if (blockedRes.ok) setBlockedUsers(await blockedRes.json());
    } catch (error) {
      console.error("Error blocking friend:", error);
    }
  };

  const handleUnblockUser = async (blockedUser) => {
    try {
      const token = await getTokenInternal();

      if (token && blockedUser) {
        const url = `${process.env.REACT_APP_API_HOST}/api/unblock/${blockedUser.blocked_user_id}`;
        await fetch(url, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });

        // Update the UI
        setBlockedUsers((prevBlockedUsers) =>
          prevBlockedUsers.filter(
            (user) => user.blocked_user_id !== blockedUser.blocked_user_id
          )
        );
      }
    } catch (error) {
      console.error("Error unblocking user:", error);
    }
  };

  const handleShowBlockedUsers = () => {
    setShowBlockedUsers(!showBlockedUsers);
  };

  const handleToggleTrust = async (friendAccountId, isTrusted) => {
    try {
      const token = await getTokenInternal();
      const url = `${process.env.REACT_APP_API_HOST}/api/trusted-claimers/${isTrusted ? "revoke" : "grant"}/${friendAccountId}`;
      await fetch(url, {
        method: isTrusted ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token.access_token}` },
      });
      setGrantedByMeIds((prev) =>
        isTrusted ? prev.filter((id) => id !== friendAccountId) : [...prev, friendAccountId]
      );
    } catch (error) {
      console.error("Error toggling trust:", error.message);
    }
  };

  useEffect(() => {
    if (selectedFriend && friendRequestsSent.has(selectedFriend.id)) {
      const timer = setTimeout(() => {
        setSelectedFriend(null); // hide the message
      }, 3000);

      return () => clearTimeout(timer); // clean up on unmount or dependency change
    }
  }, [selectedFriend, friendRequestsSent]);

  if (tokenChecked && !isLoggedIn) return <LoginRequired />;

  return (
    <div>
      <Nav />
      <div className="p-4">

        {/* Search bar */}
        <div className="mb-6">
          <span className="relative inline-block">
            <input
              type="text"
              placeholder="Search for friends"
              className="border border-gray-300 p-2 pr-8 rounded-md focus:outline-none focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedFriend(null);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 font-bold"
                aria-label="Clear search"
                title="Clear search"
              >
                ×
              </button>
            )}
          </span>

          {searchResults.length > 0 && (
            <ul className="mt-2 max-w-md">
              {searchResults
                .filter((friend) => friend.username !== currentUser)
                .map((friend) => (
                  <li
                    key={friend.id}
                    className="flex items-center justify-between bg-white border border-gray-300 p-2 rounded-md mt-2"
                  >
                    <span className="font-semibold text-gray-800">{friend.username}</span>
                    <div className="flex gap-2">
                      {!friendRequestsSent.has(friend.id) ? (
                        <button
                          className="bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 text-sm"
                          onClick={() => handleFriendSelect(friend)}
                        >
                          Add Friend
                        </button>
                      ) : (
                        <span className="text-green-600 text-sm font-semibold">Request Sent ✓</span>
                      )}
                      <button
                        className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 text-sm"
                        onClick={() => handleSearchedBlockUser(friend)}
                      >
                        Block
                      </button>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>

        {/* 3-column grid: Friends | Pending Received | Sent Requests */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Column 1 — Friends */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h2 className="text-white text-lg font-bold mb-3 border-b border-gray-600 pb-2">
              Friends{" "}
              {allFriends.length > 0 && (
                <span className="text-gray-400 text-sm font-normal">({allFriends.length})</span>
              )}
            </h2>
            {allFriends.length === 0 ? (
              <p className="text-gray-400 text-sm">No friends yet — search for someone above!</p>
            ) : (
              <ul className="space-y-3">
                {allFriends.map((friend) => {
                  const friendAccountId = resolveFriendAccountId(friend);
                  const iTrustThem = grantedByMeIds.includes(friendAccountId);
                  const theyTrustMe = whoTrustsMeIds.includes(friendAccountId);
                  return (
                    <li key={friend.id} className="bg-gray-700 rounded-md p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-white font-semibold truncate">
                            {friend.friend_username || "Unknown User"}
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            Discord:{" "}
                            {friend.visible ? friend.discord_name || "N/A" : "N/A"}
                          </p>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {iTrustThem && (
                              <span className="text-xs bg-green-700 text-green-200 px-2 py-0.5 rounded-full">
                                Trusted
                              </span>
                            )}
                            {theyTrustMe && (
                              <span className="text-xs bg-blue-700 text-blue-200 px-2 py-0.5 rounded-full">
                                Trusts You
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end shrink-0">
                          <div className="relative flex items-center gap-1">
                            <button
                              className={`text-xs px-2 py-1 rounded-md text-white ${
                                iTrustThem
                                  ? "bg-yellow-600 hover:bg-yellow-700"
                                  : "bg-green-600 hover:bg-green-700"
                              }`}
                              onClick={() => handleToggleTrust(friendAccountId, iTrustThem)}
                            >
                              {iTrustThem ? "Revoke Trust" : "Grant Trust"}
                            </button>
                            <div
                              className="group relative trust-info-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTrustInfoOpenId(
                                  trustInfoOpenId === friend.id ? null : friend.id
                                );
                              }}
                            >
                              <Info
                                size={14}
                                className="text-gray-400 hover:text-gray-200 cursor-pointer"
                              />
                              <div
                                className={`absolute right-0 top-full mt-1 w-56 p-2 rounded-md bg-gray-900 border border-gray-700 text-gray-200 text-xs shadow-lg z-20 ${
                                  trustInfoOpenId === friend.id
                                    ? "block"
                                    : "hidden group-hover:block"
                                }`}
                              >
                                {TRUST_EXPLANATION}
                              </div>
                            </div>
                          </div>
                          {confirmRemoveId === friend.id ? (
                            <div className="flex flex-col gap-1 items-end">
                              <span className="text-yellow-300 text-xs font-semibold">Remove friend?</span>
                              <div className="flex gap-1">
                                <button
                                  className="text-xs bg-red-700 hover:bg-red-800 text-white px-2 py-1 rounded-md"
                                  onClick={() => { handleRemoveFriendRequest(friend); setConfirmRemoveId(null); }}
                                >
                                  Yes
                                </button>
                                <button
                                  className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded-md"
                                  onClick={() => setConfirmRemoveId(null)}
                                >
                                  No
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              className="text-xs bg-red-700 hover:bg-red-800 text-white px-2 py-1 rounded-md"
                              onClick={() => { setConfirmRemoveId(friend.id); setConfirmBlockId(null); }}
                            >
                              Remove
                            </button>
                          )}
                          {confirmBlockId === friend.id ? (
                            <div className="flex flex-col gap-1 items-end">
                              <span className="text-yellow-300 text-xs font-semibold">Block &amp; remove?</span>
                              <div className="flex gap-1">
                                <button
                                  className="text-xs bg-red-700 hover:bg-red-800 text-white px-2 py-1 rounded-md"
                                  onClick={() => handleBlockFriend(friend)}
                                >
                                  Yes
                                </button>
                                <button
                                  className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded-md"
                                  onClick={() => setConfirmBlockId(null)}
                                >
                                  No
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded-md"
                              onClick={() => { setConfirmBlockId(friend.id); setConfirmRemoveId(null); }}
                            >
                              Block
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Column 2 — Pending Received */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h2 className="text-white text-lg font-bold mb-3 border-b border-gray-600 pb-2">
              Pending{" "}
              {pendingRequests.length > 0 && (
                <span className="text-gray-400 text-sm font-normal">({pendingRequests.length})</span>
              )}
            </h2>
            {pendingRequests.length === 0 ? (
              <p className="text-gray-400 text-sm">No incoming requests.</p>
            ) : (
              <ul className="space-y-3">
                {pendingRequests.map((request) => (
                  <li key={request.id} className="bg-gray-700 rounded-md p-3">
                    <p className="text-white font-semibold mb-2">
                      {request.username || "Unknown User"}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md"
                        onClick={() => handleAcceptFriendRequest(request)}
                      >
                        Accept
                      </button>
                      <button
                        className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-md"
                        onClick={() => handleDeclineFriendRequest(request)}
                      >
                        Decline
                      </button>
                      <button
                        className="text-xs bg-red-700 hover:bg-red-800 text-white px-3 py-1 rounded-md"
                        onClick={() => handleBlockUser(request)}
                      >
                        Block
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Column 3 — Sent Requests */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h2 className="text-white text-lg font-bold mb-3 border-b border-gray-600 pb-2">
              Sent{" "}
              {sentRequests.length > 0 && (
                <span className="text-gray-400 text-sm font-normal">({sentRequests.length})</span>
              )}
            </h2>
            {sentRequests.length === 0 ? (
              <p className="text-gray-400 text-sm">No outgoing requests.</p>
            ) : (
              <ul className="space-y-3">
                {sentRequests.map((request) => (
                  <li
                    key={request.id}
                    className="bg-gray-700 rounded-md p-3 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-white font-semibold truncate">
                        {request.username || "Unknown User"}
                      </p>
                      <p className="text-gray-400 text-xs">Waiting for response…</p>
                    </div>
                    <button
                      className="text-xs bg-red-700 hover:bg-red-800 text-white px-2 py-1 rounded-md shrink-0"
                      onClick={() => handleCancelSentRequest(request)}
                    >
                      Cancel
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Blocked users */}
        <div className="mt-6">
          <button
            className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md text-sm"
            onClick={handleShowBlockedUsers}
          >
            {showBlockedUsers ? "Hide Blocked Users" : "Show Blocked Users"}
          </button>

          {showBlockedUsers && (
            <div className="mt-3 bg-gray-800 rounded-lg p-4 max-w-sm">
              <h2 className="text-white text-lg font-bold mb-3 border-b border-gray-600 pb-2">
                Blocked Users
              </h2>
              {blockedUsers.length === 0 ? (
                <p className="text-gray-400 text-sm">No blocked users.</p>
              ) : (
                <ul className="space-y-2">
                  {blockedUsers.map((blockedUser) => (
                    <li
                      key={blockedUser.blocked_user_id}
                      className="bg-gray-700 rounded-md p-3 flex items-center justify-between"
                    >
                      <span className="text-white text-sm">
                        {blockedUser.username || "Unknown User"}
                      </span>
                      <button
                        className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md"
                        onClick={() => handleUnblockUser(blockedUser)}
                      >
                        Unblock
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default FriendSearch;
